import { Button, colors } from '@insurancemenu/imenu-components-lib';
import { Delete, Edit, Mail } from '@insurancemenu/imenu-components-lib/dist/icons';
import { observer } from 'mobx-react-lite';
import React, { useState, useEffect } from 'react';
import { useRootStore } from 'store/rootStore';
import { FullUser, UserType } from 'types/userService';
import { inviteToSelfEnroll } from 'api/operations/userService';
import EmployerForm from '../EmployerForm';
import { createStyles } from './styles';

interface EmployerBlockProps {
  employer: FullUser;
  groupId: string;
  isGroupAdmin: boolean;
}

interface EmployerInfoOption {
  title: string;
  getValue: () => string | undefined;
}

interface EmployerOptionObj {
  [key: string]: EmployerInfoOption;
}

const EmployerBlock = observer(({ employer, groupId, isGroupAdmin = false }: EmployerBlockProps): JSX.Element => {
  // determines which view to show the open form or employer block
  const [isFormView, setIsFormView] = useState(false);

  const classes = createStyles(isGroupAdmin);
  const {
    additionalAccessStore: {
      editEmployerForm,
      isEditFormOpen,
      isEditEmployersModalOpen,
      setEditFormStatus,
      openEditEmployerForm,
      removeEmployer,
      editEmployerInfo,
    },
    groupSidebarStore: { setErrorToastStatus },
  } = useRootStore();

  // Reset form view when modal opens
  useEffect(() => {
    if (isEditEmployersModalOpen) {
      setIsFormView(false);
    }
  }, [isEditEmployersModalOpen]);

  const employersInfo: EmployerOptionObj = {
    name: {
      title: 'Name',
      getValue: (): string => `${employer.firstName} ${employer.middleName || ''} ${employer.lastName}`,
    },
    email: {
      title: 'Email',
      getValue: (): string => employer.email,
    },
    telephone: {
      title: 'Phone',
      getValue: (): string => `${employer.telephone}`,
    },
    title: {
      title: 'Title',
      getValue: (): string => employer.title,
    },
  };

  const openFormWithAnswers = (): void => {
    setIsFormView(true);
    openEditEmployerForm(employer.id);
  };

  const onUserUpdate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (editEmployerForm.isFormValid()) {
      await editEmployerInfo(employer.id, groupId, isGroupAdmin);
      setIsFormView(false);
    }
  };

  const onUserRemove = (): void => {
    removeEmployer(groupId, employer.id, isGroupAdmin);
  };

  const onFormClose = (): void => {
    setEditFormStatus(false);
    setIsFormView(false);
    editEmployerForm.resetForm();
  };

  const reinviteEmployer = async (): Promise<void> => {
    const { firstName = '', middleName = '', lastName = '', email = '', title = '', telephone = '' } = employer;

    const userInfo = {
      firstName,
      middleName,
      lastName,
      email,
      title,
      groupId,
      telephone,
      userType: 'EMPLOYER' as UserType,
    };
    await inviteToSelfEnroll([userInfo]);
    setErrorToastStatus(true, `${firstName} ${lastName} was successfully reinvited`);
  };

  return (
    <>
      {!isFormView ? (
        <div className={classes.employerWrapper} id={employer.id}>
          <div className={classes.employerBlock}>
            {Object.values(employersInfo).map(({ title, getValue }) => {
              return (
                <div key={title}>
                  <p className={classes.employerBlockKey}>{title}</p>
                  <p className={classes.employerBlockValue}>{getValue()}</p>
                </div>
              );
            })}
            <div key={employer.status}>
              <p className={classes.employerBlockKey}>
                Status: <span className={classes.statusValue}>{employer.status}</span>
              </p>
              <Button
                onClick={reinviteEmployer}
                className={classes.statusActionBtn}
                type="text"
                color="primary"
                leftIconProp={<Mail />}
                id="reinvite-employer-button"
              >
                Reinvite
              </Button>
            </div>
          </div>

          <div>
            <Button
              onClick={openFormWithAnswers}
              className={classes.actionBtn}
              disabled={isEditFormOpen}
              type="text"
              color="primary"
              leftIconProp={<Edit />}
              id="edit-employer-button"
            >
              Edit
            </Button>
            <Button
              className={classes.actionBtn}
              disabled={isEditFormOpen}
              type="text"
              color="custom"
              customColor={colors.red[600]}
              leftIconProp={<Delete />}
              onClick={onUserRemove}
              id="remove-employer-button"
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <EmployerForm
          formClassName={classes.editEmployerForm}
          form={editEmployerForm}
          onAction={onUserUpdate}
          onClose={onFormClose}
          actionBtnText={isGroupAdmin ? 'Update Group Admin' : 'Update Employer'}
          formButtonIcon={<Edit />}
          userId={employer.id}
          isEmailDisabled
        />
      )}
    </>
  );
});

export default EmployerBlock;
